'use client';

import * as React from 'react';
import { Check, ChevronsUpDown, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useReceiptBuilder } from '@/hooks/useReceiptBuilder';
import {
  brandTemplates,
  templateCategories,
  type TemplateCategory,
} from '@/lib/templates/brands';
import type { ReceiptTemplate } from '@/lib/templates/schema';

const categoryLabels: Record<TemplateCategory, string> = {
  luxury: 'Luxury & Designer',
  tech: 'Software & Tech',
  fashion: 'Fashion & Beauty',
  food: 'Food & Restaurants',
  retail: 'Retail & Grocery',
  auto: 'Auto & Hardware',
  gas: 'Gas & Convenience',
  transport: 'Transportation',
  shipping: 'Shipping & Delivery',
  hotel: 'Hotels & Hospitality',
  fintech: 'Fintech & Payments',
  resale: 'Resale & Marketplace',
  medical: 'Medical',
  telecom: 'Telecom',
  rewards: 'Rewards & Cashback',
  charity: 'Charity & Nonprofit',
};

function buildCategoryMap(): Map<TemplateCategory, ReceiptTemplate[]> {
  const map = new Map<TemplateCategory, ReceiptTemplate[]>();
  const categoryOrder: TemplateCategory[] = [
    'retail',
    'food',
    'luxury',
    'fashion',
    'tech',
    'fintech',
    'transport',
    'hotel',
    'auto',
    'gas',
    'shipping',
    'resale',
    'telecom',
    'rewards',
    'charity',
    'medical',
  ];

  for (const cat of categoryOrder) {
    const ids = templateCategories[cat];
    const templates = brandTemplates.filter((t) => ids.includes(t.id));
    if (templates.length > 0) {
      map.set(cat, templates);
    }
  }

  return map;
}

export function TemplateSwitcher() {
  const [open, setOpen] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [pendingTemplate, setPendingTemplate] =
    React.useState<ReceiptTemplate | null>(null);

  const template = useReceiptBuilder((s) => s.template);
  const isDirty = useReceiptBuilder((s) => s.isDirty);
  const setTemplate = useReceiptBuilder((s) => s.setTemplate);

  const categoryMap = React.useMemo(() => buildCategoryMap(), []);

  // Mobile scroll-aware focus: prevent input focus during scroll gestures
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isScrollingRef = React.useRef(false);
  const touchStartYRef = React.useRef(0);

  const handleListTouchStart = React.useCallback((e: React.TouchEvent) => {
    touchStartYRef.current = e.touches[0].clientY;
    isScrollingRef.current = false;
  }, []);

  const handleListTouchMove = React.useCallback((e: React.TouchEvent) => {
    const delta = Math.abs(e.touches[0].clientY - touchStartYRef.current);
    if (delta > 8) {
      isScrollingRef.current = true;
      // Blur the input immediately if scroll detected — prevents keyboard pop-up
      if (inputRef.current && document.activeElement === inputRef.current) {
        inputRef.current.blur();
      }
    }
  }, []);

  // Intercept pointer-down on list to stop cmdk from stealing focus to input during scroll
  const handleListPointerDown = React.useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'touch') {
      // Prevent cmdk's default behaviour of focusing the input on touch interaction
      e.preventDefault();
    }
  }, []);

  // Lock body scroll when popover is open to prevent scroll bleed on mobile
  React.useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const handleSelect = React.useCallback(
    (selected: ReceiptTemplate) => {
      if (selected.id === template.id) {
        setOpen(false);
        return;
      }

      if (isDirty) {
        setPendingTemplate(selected);
        setShowConfirm(true);
        setOpen(false);
      } else {
        setTemplate(selected);
        setOpen(false);
      }
    },
    [template.id, isDirty, setTemplate]
  );

  const confirmSwitch = React.useCallback(() => {
    if (pendingTemplate) {
      setTemplate(pendingTemplate);
      setPendingTemplate(null);
    }
    setShowConfirm(false);
  }, [pendingTemplate, setTemplate]);

  const cancelSwitch = React.useCallback(() => {
    setPendingTemplate(null);
    setShowConfirm(false);
  }, []);

  return (
    <>
      <div className="px-3 py-2.5 border-b bg-muted/30 touch-none">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between h-9 text-sm font-normal"
            >
              <span className="flex items-center gap-2 min-w-0">
                <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">
                  {template.name || 'Select template...'}
                </span>
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
            sideOffset={4}
          >
            <Command
              filter={(value, search) => {
                const t = brandTemplates.find((bt) => bt.id === value);
                if (!t) return 0;
                const haystack =
                  `${t.name} ${t.brand || ''}`.toLowerCase();
                const needle = search.toLowerCase();
                return haystack.includes(needle) ? 1 : 0;
              }}
            >
              <CommandInput ref={inputRef} placeholder="Search templates..." autoFocus={false} />
              <CommandList
                className="overscroll-contain touch-pan-y"
                onTouchStart={handleListTouchStart}
                onTouchMove={handleListTouchMove}
                onPointerDown={handleListPointerDown}
              >
                <CommandEmpty>No templates found.</CommandEmpty>
                {Array.from(categoryMap.entries()).map(
                  ([category, templates]) => (
                    <CommandGroup
                      key={category}
                      heading={categoryLabels[category]}
                    >
                      {templates.map((t) => (
                        <CommandItem
                          key={t.id}
                          value={t.id}
                          onSelect={() => handleSelect(t)}
                        >
                          <Check
                            className={cn(
                              'h-3.5 w-3.5 shrink-0',
                              template.id === t.id
                                ? 'opacity-100'
                                : 'opacity-0'
                            )}
                          />
                          <span className="truncate">{t.name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch template?</AlertDialogTitle>
            <AlertDialogDescription>
              Your current edits will be lost. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelSwitch}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmSwitch}>
              Switch
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
